import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useWelcomePopupStore } from '#modules/user/features/onboarding/model/welcomePopupStore';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import {
  trackWebPushDialogOutcome,
  trackWebPushPromptAllow,
  trackWebPushPromptDismiss,
  trackWebPushPromptLater,
} from '#features/analytics/utils/pushPermissionFunnel';
import {
  isUpdatePromptVisible,
  subscribeUpdatePrompt,
} from '#features/app-update/utils/statusFlags';
import { useCallStore } from '#features/calls/model/callStore';
import { isPostNudgeVisible, subscribeScreenFlags } from '#features/store-review/model/screenFlags';
import { envConfig } from '#constants/envConfig';
import { useSSR } from '#hooks/useSSR';
import {
  markPushAttentionAsked,
  readPushAttentionAskedAt,
  type PushAttentionReason,
} from '../utils/pushAttention';
import { isPushPromptArmed, subscribePushPrompt } from '../utils/pushPrompt';
import { resolveWebPushAsk } from '../utils/webPushAsk';
import { detectWebPushState, readNotificationPermission } from '../utils/webPushState';
import { usePushNotifications } from './usePushNotifications';

/**
 * Пауза между действием и вопросом.
 *
 * `notePushWorthyAction()` срабатывает внутри `.then()` мутации, то есть РАНЬШЕ, чем на экране
 * появится то, что человек этим действием создал. Без паузы карточка легла бы поверх только что
 * отправленного сообщения или экрана «заявка создана», а рефлекторно закрытый вопрос стоит
 * недели молчания и не приносит ничего.
 */
const SETTLE_MS = 2_500;

/**
 * Освободился ли человек прямо сейчас.
 *
 * Три условия, и каждое стоило бы нам согласия:
 *
 * - вкладка видима. Карточка в фоновой вкладке всё равно запишет отметку и промолчит неделю,
 *   при этом её никто не увидит;
 * - курсор не стоит в поле ввода. Отправленное сообщение почти всегда не последнее: человек
 *   печатает дальше, и модалка посреди фразы забирает клик, а на телефоне ещё и клавиатуру.
 *   Рефлекс в ответ один, закрыть, и это стоит недели молчания;
 * - на экране нет другого диалога. Поимённые гейты в самом хуке закрывают известные окна, а эта
 *   проверка ловит все остальные, включая те, которых ещё не написали.
 *
 * Своя карточка сюда попасть не может: спрашиваем только пока она не показана.
 */
const isScreenQuiet = () => {
  if (typeof document === 'undefined') {
    return true;
  }
  if (document.visibilityState !== 'visible') {
    return false;
  }
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
  ) {
    return false;
  }

  return !document.querySelector('[role="dialog"][data-state="open"]');
};

/**
 * Решение «спрашивать ли разрешение на уведомления» целиком здесь, чтобы компонент остался
 * разметкой, а поведение проверялось без Chakra.
 *
 * Все чтения безусловные и в постоянном порядке: правила хуков не терпят раннего выхода, а
 * гейты всё равно должны считаться до того, как мы решим молчать.
 */
export const useWebPushPrompt = () => {
  const { isBrowser } = useSSR();
  const { profile } = useProfile();
  const { enableNotifications } = usePushNotifications();
  const callPhase = useCallStore((state) => state.phase);
  const welcomePopupOpen = useWelcomePopupStore((state) => state.open);

  // Третий аргумент обязателен у каждого: без серверного снимка React бросает
  // «getServerSnapshot missing» прямо в SSR.
  const gateBusy = useSyncExternalStore(subscribeUpdatePrompt, isUpdatePromptVisible, () => false);
  const armed = useSyncExternalStore(subscribePushPrompt, isPushPromptArmed, () => false);
  const postNudgeVisible = useSyncExternalStore(
    subscribeScreenFlags,
    isPostNudgeVisible,
    () => false
  );

  // Ленивый инициализатор: на сервере `Notification` трогать нельзя, а первый клиентский рендер
  // должен совпасть с серверным: там хук вернёт null и шторка не покажется.
  const [permission, setPermission] = useState<NotificationPermission | null>(() =>
    readNotificationPermission()
  );
  /** Закрыта в этом сеансе вкладки. Отметку на неделю пишут обработчики, а не это состояние. */
  const [dismissed, setDismissed] = useState(false);
  const [settled, setSettled] = useState(false);

  // Разрешение меняют на экране браузера, а не на нашем: перечитываем при возврате фокуса, как
  // это уже делает баннер в настройках.
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }
    const refresh = () => setPermission(readNotificationPermission());
    window.addEventListener('focus', refresh);

    return () => window.removeEventListener('focus', refresh);
  }, []);

  /*
   * Ждём не «прошло N секунд», а «человек освободился»: см. `isScreenQuiet`. Если сейчас
   * нельзя, ждём ещё столько же и проверяем снова, потому что повод никуда не делся, а
   * подходящий момент почти наверняка наступит через несколько секунд.
   */
  useEffect(() => {
    if (!armed || settled) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    const check = () => {
      if (isScreenQuiet()) {
        setSettled(true);
        return;
      }
      timer = setTimeout(check, SETTLE_MS);
    };

    timer = setTimeout(check, SETTLE_MS);

    return () => clearTimeout(timer);
  }, [armed, settled]);

  const state = detectWebPushState({ permission, inBrowser: isBrowser });

  /*
   * Причины молчать, в порядке цены ошибки:
   *
   * - нативная оболочка: там свой разговор ведёт PushOnboarding;
   * - до гидратации и без профиля: спрашивать не у кого;
   * - экран занят чужим разговором: обновление, входящий звонок, приветствие новичку, окно
   *   «назначь сессию» после созданной заявки. Последнее особенно важно: то же самое создание
   *   заявки и открывает это окно, и поднимает наш повод;
   * - в этом сеансе уже закрыли;
   * - экран ещё не улёгся после действия.
   *
   * Новичок специально НЕ исключён, в отличие от приложения: человек, только что выложивший
   * первую заявку, это самое ценное согласие, какое у нас бывает, а требование «сначала действие»
   * и так держит нас подальше от его первых секунд.
   */
  const busy =
    envConfig.IS_NATIVE ||
    !isBrowser ||
    !profile?.id ||
    gateBusy ||
    (callPhase !== 'idle' && callPhase !== 'ended') ||
    welcomePopupOpen ||
    postNudgeVisible ||
    dismissed ||
    !settled;

  const reason = busy
    ? null
    : resolveWebPushAsk({
        state,
        armed,
        askedAt: readPushAttentionAskedAt('prompt'),
        now: Date.now(),
      });

  /**
   * Главная кнопка. Самое хрупкое место всей фичи.
   *
   * Считаем событие ПЕРВЫМ (`tracking.event` это синхронный вызов gtag в try/catch, жест им не
   * тратится), затем поднимаем диалог, и между кликом и `Notification.requestPermission()` не
   * ждём ничего: Firefox и Safari требуют «свежего» жеста и иначе молча вернут 'default'.
   *
   * Результат диалога перечитываем после, а не выводим из промиса: `registerNotifications`
   * внутри вызывается без await, поэтому разрешившийся промис ещё не означает, что токен есть.
   */
  const allow = useCallback(() => {
    if (!reason) {
      return;
    }
    trackWebPushPromptAllow(reason);
    void enableNotifications().finally(() => {
      const answer = readNotificationPermission();
      if (answer) {
        trackWebPushDialogOutcome(answer);
      }
      setPermission(answer);
      /*
       * Молчим неделю после ЛЮБОГО ответа, кроме согласия.
       *
       * «Блокировать» очевидно: без отметки человека, который только что нас заблокировал,
       * встречала бы та же карточка. Но так же важен и 'default', то есть закрытый крестиком
       * диалог браузера. Отметки там нет ни в состоянии (разрешение осталось 'default'), ни в
       * памяти вкладки, которая живёт до перезагрузки, поэтому без этой строки мы поднимали бы
       * настоящий диалог браузера при каждом следующем заходе. Chrome считает такие закрытия и
       * после третьего перестаёт его показывать вовсе, то есть мы своими руками пришли бы к
       * блокировке, ради ухода от которой всё и делалось.
       *
       * Цена ошибки в другую сторону есть, и она мала: 'default' возвращают ещё и Firefox с
       * Safari, когда диалог не показан из-за потерянного жеста. Такой человек промолчит неделю,
       * ничего не увидев, зато сама поломка останется видимой в событии web_push_dialog_outcome.
       */
      if (answer && answer !== 'granted') {
        markPushAttentionAsked('prompt', Date.now());
      }
      setDismissed(true);
    });
  }, [enableNotifications, reason]);

  /** «Не сейчас» это ответ, а не ошибка. Молчим неделю. */
  const later = useCallback(() => {
    if (reason) {
      markPushAttentionAsked(reason, Date.now());
      trackWebPushPromptLater(reason);
    }
    setDismissed(true);
  }, [reason]);

  /**
   * Крестик, Escape, клик мимо тоже пишут отметку на неделю.
   *
   * Осознанное расхождение с приложением, где клик по фону не сохраняет ничего. Приложение
   * запускают редко, а вкладок в браузере человек открывает десятки за день: карточка,
   * возвращающаяся в каждой новой вкладке, это ровно та назойливость, от которой мы уходим.
   */
  const dismiss = useCallback(() => {
    if (reason) {
      markPushAttentionAsked(reason, Date.now());
      trackWebPushPromptDismiss(reason);
    }
    setDismissed(true);
  }, [reason]);

  return { allow, dismiss, later, reason } satisfies {
    allow: () => void;
    dismiss: () => void;
    later: () => void;
    reason: PushAttentionReason | null;
  };
};
